import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {DbActiveModal} from "../../../modules/modal/modal-ref";
import {filter, first, switchMap} from "rxjs/operators";
import {HostingService} from "../../../websites/website/hosting/hosting.service";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {User} from "../../../user/user";
import {IWebsiteCloneData, IWebsiteCloneForm, WebsiteCloneData} from "./website-clone-data.model";
import {DbModalService} from "../../../modules/modal/modal";
import {CreateWebsiteSuccessComponent} from "../create-website-success/create-website-success.component";
import {
    ManualMigrationDialogComponent
} from "../../../shared/components/dialogs/migration/manual-migration-dialog/manual-migration-dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {UserSubscription} from "../../../user/user-subscription";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {GetResellerMainDomain} from "../../../store";

export type WebsiteCloneStep = 'cloneConfigs' | 'cloneProcess';

export interface ICloneWebsiteChangeStepEvent {
    step: WebsiteCloneStep;
    options?: {
        websiteData?: IWebsiteCloneForm,
        websiteUrl?: string,
        dataCenter?: string
    };
}

@Component({
    selector: 'db-clone-website',
    templateUrl: './clone-website.component.html',
    styleUrls: ['./clone-website.component.scss'],
    standalone: false
})
export class CloneWebsiteComponent implements OnInit {

    @Input() websiteIds: { live: number, dev: number | null };
    @Input() originalDomain: string;
    @Input() hostedWebsitesCount: number;
    @Input() hostedWebsitesLimit: number;
    @Input() userSubscription: UserSubscription;
    public step: WebsiteCloneStep = 'cloneConfigs';
    public user: User;
    configHostname: any;
    cloneWebsiteUrl: string;
    dataCenter: string;

    private _destroyRef = inject(DestroyRef);

    constructor(
        private _store: Store<fromStore.DbStoreState>,
        private _activeModal: DbActiveModal,
        private _modalService: DbModalService,
        private _hostingService: HostingService,
        private _dialogService: DialogService
    ) {
    }

    ngOnInit() {
        this._store.dispatch(new GetResellerMainDomain());
        this.getDomain();
        this._runInitialSubscriptions();
        this._getWebsites();
    }

    private getDomain() {
        this._store.select(fromStore.getMainDomain)
            .pipe(
                first(domain => !!domain),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(domain => {
            this.configHostname = domain?.main_domain;
        });
    }

    private _runInitialSubscriptions(): void {
        this._store.select(fromStore.getUserLoaded)
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() => this._store.select(fromStore.getUser)),
                first()
            )
            .subscribe(user => {
                this.user = user;
            });
    }

    private _getWebsites(): void {
        this._store.select(fromStore.getWebsites)
            .pipe(
                first(websites => !!websites)
            )
            .subscribe(websites => {
                this.hostedWebsitesCount = websites.filter(website => !website.parentId && website.isActive && website.type === 'live' && !!website.domain_hash).length;
            });
    }

    public resize(size: [number, number]): void {
        this._activeModal.resize(...size);
    }

    public changeStep(event: ICloneWebsiteChangeStepEvent): void {
        if (event.options && event.options.websiteData) {
            this.cloneWebsiteUrl = event.options.websiteUrl;
            this.dataCenter = event.options.dataCenter;
            event.step === 'cloneProcess' && this._cloneWebsite(this._websiteDataMap(event.options.websiteData));
        }
    }

    private _cloneWebsite(websiteData: IWebsiteCloneData): void {
        this._hostingService.autoMigration(websiteData, websiteData.websiteId)
            .pipe(
                first(response => !!response),
            )
            .subscribe(response => {
                if (response.success) {
                    this.step = 'cloneProcess';
                } else {
                    this._activeModal.close({status: 'fail', data: websiteData});
                    // this.showMigrationFailedComponent();
                }
            });
    }

    /** map new Data into old website clone data */
    private _websiteDataMap(websiteData: IWebsiteCloneForm): IWebsiteCloneData {
        return new WebsiteCloneData(websiteData);
    }

    public showCreateWebsiteSuccessComponent(websiteId: number): void {
        this._store.select(fromStore.getWebsite(websiteId))
            .pipe(first(website => !!website))
            .subscribe(website => {
                this._activeModal.close();
                const modal = this._modalService.open(CreateWebsiteSuccessComponent);
                modal.componentInstance.website = website;
                modal.componentInstance.isClone = true;
            });
    }

    public showManualMigrationModal(): void {
        this._activeModal.close();
        this._dialogService.addDialog(ManualMigrationDialogComponent, {ErrMessage: true});
    }
}
